/*
 * The one place the running promotion is described.
 *
 * The backend decides, not this file. Checkout auto-applies a Stripe coupon
 * (STRIPE_DEFAULT_COUPON in the backend's environment), and GET /promo reports
 * whether that coupon is live right now and what it is worth. Every banner in
 * the panel reads that answer, so turning the discount on, off or extending it
 * is an env change on the backend and nothing here.
 *
 * It used to be a hardcoded end date in this file. That is exactly how the
 * banners and checkout drifted apart: the date here and the date on the server
 * were two numbers kept in sync by hand, and a banner promising 10% off while
 * checkout charged full price is worse than no banner at all.
 */

import { computed, reactive, readonly } from "vue";
import API_URL from "@/globals/api";

const state = reactive({
  loaded: false,
  active: false,
  percentOff: null,
  amountOff: null, // minor units, only for a fixed-amount coupon
  currency: null,
  endsOn: null, // YYYY-MM-DD, inclusive, UTC
});

let request = null;

/*
 * One request per page load, shared by every surface that asks.
 *
 * A bare `fetch` with no custom headers on purpose: it is a simple CORS request,
 * so no preflight, and it carries no auth — the endpoint is public, because the
 * promo has to reach logged-out visitors on the pricing and register pages.
 *
 * Any failure resolves to "no promo". Claiming a discount we could not confirm
 * is the one outcome this must never produce.
 */
export const loadPromo = () => {
  if (!request) {
    request = fetch(`${API_URL}/promo`)
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (!body || body.active !== true) return;
        state.active = true;
        state.percentOff = Number(body.percent_off) || null;
        state.amountOff = Number(body.amount_off) || null;
        state.currency = body.currency || null;
        state.endsOn = body.ends_on || null;
      })
      .catch(() => {})
      .finally(() => {
        state.loaded = true;
      });
  }
  return request;
};

/*
 * `ends_on` is re-checked here as well as on the server: a tab left open over
 * the end date would otherwise keep advertising a discount the next checkout no
 * longer gets. Date-only UTC comparison, inclusive, same as the backend.
 */
const isLive = () =>
  state.active &&
  (!state.endsOn || new Date().toISOString().slice(0, 10) <= state.endsOn);

/*
 * "10% off", or "€5 off" for a fixed-amount coupon — or null when the backend
 * reports a discount without a number (Stripe could not be asked, and checkout
 * is trusting the configured coupon). Callers word that case themselves rather
 * than print a figure nobody confirmed.
 */
const label = () => {
  if (state.percentOff) return `${state.percentOff}% off`;
  if (state.amountOff) {
    const symbol = !state.currency || state.currency === "eur" ? "€" : "";
    return `${symbol}${(state.amountOff / 100).toFixed(2).replace(/\.00$/, "")} off`;
  }
  return null;
};

/* What the price will be at checkout, or null when it can't be worked out. */
const applyTo = (price) => {
  const amount = Number(price);
  if (!isLive() || !Number.isFinite(amount) || amount <= 0) return null;
  if (state.percentOff) {
    return Math.round(amount * (100 - state.percentOff)) / 100;
  }
  if (state.amountOff) {
    return Math.max(0, Math.round(amount * 100 - state.amountOff) / 100);
  }
  return null;
};

export const usePromo = () => {
  loadPromo();
  return {
    promo: readonly(state),
    isActive: computed(isLive),
    label: computed(label),
    applyTo,
  };
};

/* The invite every surface uses — the Discord tile on the support page among
   them. Make sure whatever is set here is a *permanent* invite: a per-session
   one expires, which would leave a bookmarked or cached page pointing at a dead
   door. */
export const DISCORD_INVITE_URL = "https://discord.gg/tcyQAZFHTD";
